/* istanbul ignore file */
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { CriteoProductsRecoSlider, PrudsysAddToBasketRecoSlider } from '@packages/recommendations';
import { Box, Button, Card, CardContent, ErrorBoundary } from '@packages/shared';
import type { FC } from 'react';
import { Suspense } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useHashParameters } from '../../../hooks/useHashParameters/useHashParameters';
import { AddedProduct, AddedProductLoadingFallback } from './AddedProduct';

const AtbLayerFragment = graphql(/* GraphQL */ `
  fragment AtbLayerFragment on Product {
    availability {
      state
    }
    price {
      value
    }
    defaultCategory
    orderNumber
    brand {
      name
    }
    hasSeries
  }
`);

const fallbackRDEHeadlines = defineMessages({
  criteoFallback: {
    defaultMessage: 'Gesponserte Artikel',
    id: 'rde.criteo-products.headline-fallback',
  },
});

type AtbLayerProps = {
  lineItems?: Array<string>;
  productData: FragmentType<typeof AtbLayerFragment>;
};

export const AtbLayer: FC<AtbLayerProps> = ({ lineItems, productData }) => {
  const { brand, defaultCategory, orderNumber, hasSeries, price, availability } = getFragmentData(
    AtbLayerFragment,
    productData,
  );
  const { category = '' } = useHashParameters();
  const { formatMessage } = useIntl();

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: [1.5, 1.5, 3],
        bgcolor: 'grey.light',
      }}
    >
      <Card>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <ErrorBoundary
            fallback={(reset) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                Produkt konnte nicht geladen werden...{' '}
                <Button onClick={reset}>Erneut versuchen</Button>
              </Box>
            )}
          >
            <Suspense fallback={<AddedProductLoadingFallback />}>
              <AddedProduct lineItems={lineItems} />
            </Suspense>
          </ErrorBoundary>
        </CardContent>
      </Card>

      <PrudsysAddToBasketRecoSlider
        brand={brand?.name ?? ''}
        categoryIds={[defaultCategory ?? category]}
        productId={orderNumber}
        elementIndex={hasSeries ? 4 : 3}
        pageTemplate="detailview"
        title={formatMessage({
          defaultMessage: 'Andere Kunden kauften auch',
          id: 'addToBasket.layer.rde.prudsys.headline-fallback',
        })}
      />

      <CriteoProductsRecoSlider
        available={availability.state === 'NOT_AVAILABLE' ? 0 : 1}
        price={parseFloat(price.value.toFixed(2))}
        productId={orderNumber}
        fetchOnThreshold
        elementIndex={hasSeries ? 2 : 1}
        pageTemplate="detailview"
        title={formatMessage(fallbackRDEHeadlines.criteoFallback)}
        key={`${orderNumber}${2}`}
      />
    </Box>
  );
};
